import { type DependencyList, useEffect } from 'react';

import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { offerOrdersGetListRequested } from 'src/actions';
import { GetOfferOrderListQueryParamsDto } from 'src/dtos';
import { OfferOrder } from 'src/models';

import { CustomHookBaseOptions } from '../models/common.model';

import { useAccountSelector } from './useAccountSelector';

interface UseOfferOrdersReturnType {
  offerOrderList: OfferOrder[];
  isOfferOrderListLoading: boolean;
  hasOfferOrderListError?: boolean;
  offerOrderListError?: string;
  offerOrderListTotal: number;
}

export interface UseOfferOrdersOptions extends CustomHookBaseOptions {
  queryParams?: GetOfferOrderListQueryParamsDto;
  refetchOnChange?: boolean;
  dependency?: DependencyList;
}

const EMPTY_OFFER_ORDERS: OfferOrder[] = [];

export const useOfferOrders = (
  { skip, refetchOnChange, queryParams, dependency = [] }: UseOfferOrdersOptions = { skip: false },
): UseOfferOrdersReturnType => {
  const dispatch = useDispatch();
  const offerOrderList: OfferOrder[] | undefined = useSelector((state: any) => state.offerOrders.list?.data?.data);
  const isOfferOrderListLoading = useSelector((state: any) => Boolean(state.offerOrders.list?.__requested));
  const hasOfferOrderListFailed = useSelector((state: any) => Boolean(state.offerOrders.list?.__failed));
  const offerOrderListError: string | undefined = useSelector((state: any) => state.offerOrders?.list?.__message);
  const payload = useSelector((state: any) => state.offerOrders.list?.payload);
  const offerOrderListTotal: number = useSelector((state: any) => state.offerOrders.list?.data?.total ?? 0);
  const { account } = useAccountSelector();

  const fetchList = () => {
    if (!account?.accountId) {
      return;
    }

    dispatch(offerOrdersGetListRequested({ params: { accountId: account.accountId }, queryParams }));
  };

  useEffect(() => {
    if (skip) {
      return;
    }

    if (!offerOrderList && !isOfferOrderListLoading && !hasOfferOrderListFailed) {
      fetchList();

      return;
    }

    // when refetchOnChange is true, we want to fetch the list again or if is not a duplicate request, we want to fetch the list again
    if (refetchOnChange || !isEqual(payload, { params: { accountId: account?.accountId }, queryParams })) {
      fetchList();
    }
  }, dependency);

  return {
    offerOrderList: Array.isArray(offerOrderList) ? offerOrderList : EMPTY_OFFER_ORDERS,
    isOfferOrderListLoading,
    hasOfferOrderListError: hasOfferOrderListFailed,
    offerOrderListError,
    offerOrderListTotal,
  };
};
